import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Grid, Paper, Stack } from "@mui/material";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import RefreshIcon from "@mui/icons-material/Refresh";
import isEqual from "lodash.isequal";
import { useNavigate } from "react-router-dom";
import { usePreload } from "../../../contexts/DataContext/PreloadContext";
import { useUnsavedChangesGuard } from "../../../contexts/hooks/useUnsavedChangesGuard";
import { emptyLocalizedFields } from "../../../config/localization";
import { uploadMedia } from "../../../services/mediaService";
import { UploadType } from "../../../types/enum";
import LocalizedInput from "../LocalizedInput";
import TextInput from "../TextInput";
import { CardHeading, cardSx } from "../CardHeading";
import { StickyFormActions } from "../StickyFormActions";

export const UnitForm = ({ props, onSave, onRefresh }) => {
  const navigate = useNavigate();
  const { openImageDialog, fileUploadDialog, showNotification } = usePreload();
  const saveInProgress = useRef(false);
  const defaultUnit = useMemo(
    () => ({
      unitCode: props.row.unitCode ?? "",
      unitCodeAlternative: props.row.unitCodeAlternative ?? "",
      unitCodeOld: props.row.unitCodeOld ?? "",
      unitImage: props.row.unitImage ?? "",
      unitResourceId: props.row.unitResourceId ?? "",
      unitName: props.row.unitName || emptyLocalizedFields,
      unitAbbreviation: props.row.unitAbbreviation || emptyLocalizedFields,
      unitDisplayName: props.row.unitDisplayName || emptyLocalizedFields,
      unitDescription: props.row.unitDescription || emptyLocalizedFields,
      unitToolTip: props.row.unitTooltip || emptyLocalizedFields,
    }),
    [props.row],
  );
  const [unit, setUnit] = useState(defaultUnit);
  const [original, setOriginal] = useState(defaultUnit);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    setUnit(defaultUnit);
    setOriginal(defaultUnit);
  }, [defaultUnit]);

  const hasChanges = !isEqual(unit, original);
  const busy = Boolean(props.loading || props.uploading || uploadingImage);
  const { confirmLeave } = useUnsavedChangesGuard(hasChanges);

  const save = async (nextUnit, resetAfterCreate = false) => {
    if (saveInProgress.current) return false;
    saveInProgress.current = true;
    try {
      const saved = await onSave(nextUnit);
      if (saved !== true) return false;
      if (!props.isUpdate && resetAfterCreate) {
        setUnit(defaultUnit);
        setOriginal(defaultUnit);
      } else {
        setUnit(nextUnit);
        setOriginal(nextUnit);
      }
      return true;
    } finally {
      saveInProgress.current = false;
    }
  };

  const openUploader = () => {
    fileUploadDialog.show({
      title: "Upload Image",
      imageLink: unit.unitImage,
      handleImageUpload: async ({ uploadType, imageFile, imageLink }) => {
        setUploadingImage(true);
        fileUploadDialog.setLoading(true);
        try {
          const nextUnit = {
            ...unit,
            unitResourceId: "",
            unitImage: imageLink ?? unit.unitImage ?? "",
          };
          if (uploadType === UploadType.FILE) {
            const result = await uploadMedia(imageFile);
            if (!result?.resourceId || !result?.resourceUrl) {
              throw new Error("Image upload failed");
            }
            nextUnit.unitResourceId = result.resourceId;
            nextUnit.unitImage = result.resourceUrl;
          }
          setUnit(nextUnit);
          if (props.isUpdate) await save(nextUnit);
        } catch (error) {
          console.error(error);
          showNotification("Image upload failed.", "error");
        } finally {
          fileUploadDialog.setLoading(false);
          fileUploadDialog.hide();
          setUploadingImage(false);
        }
      },
    });
  };

  const update = (field) => (event) => {
    setUnit((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleImageUrlChange = (event) => {
    const imageUrl = event.target.value;
    setUnit((prev) => ({
      ...prev,
      unitImage: imageUrl,
      unitResourceId: "",
    }));
  };

  const handleCancel = async () => {
    if (!busy && (await confirmLeave())) navigate("/unitsnew");
  };

  const handleRefresh = async () => {
    if (await confirmLeave()) onRefresh();
  };

  const handleSave = () => {
    if (!busy && hasChanges) save(unit, true);
  };

  const localized = (field, label, multiline = false) => (
    <Grid size={{ xs: 12, md: 6 }}>
      <LocalizedInput
        variant="modern"
        label={label}
        value={unit[field]}
        onChange={(next) => setUnit((prev) => ({ ...prev, [field]: next }))}
        multiline={multiline}
        rows={2}
        disabled={busy}
      />
    </Grid>
  );

  return (
    <Box
      sx={{
        "& input, & textarea, & button": {
          scrollMarginBottom: { xs: 168, sm: 96 },
        },
      }}
    >
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="unit-details-heading"
            sx={cardSx}
          >
            <CardHeading
              id="unit-details-heading"
              title="Unit details"
              helper="Identify the unit and manage its symbol."
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  label="Code"
                  required
                  value={unit.unitCode}
                  disabled={props.isUpdate || busy}
                  helperText={
                    props.isUpdate
                      ? "The unit code cannot be changed after creation"
                      : undefined
                  }
                  onChange={update("unitCode")}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  label="Alternative code"
                  value={unit.unitCodeAlternative}
                  disabled={busy}
                  onChange={update("unitCodeAlternative")}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextInput
                  label="Old code"
                  value={unit.unitCodeOld}
                  disabled={busy}
                  onChange={update("unitCodeOld")}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ sm: "center" }}
                >
                  {unit.unitImage && (
                    <Button
                      aria-label="Preview symbol"
                      onClick={() => openImageDialog(unit.unitImage)}
                      sx={{ minWidth: 48, p: 1 }}
                    >
                      <img src={unit.unitImage} alt="" width={32} height={32} />
                    </Button>
                  )}
                  <TextInput
                    label="Image URL"
                    value={unit.unitImage}
                    disabled={busy}
                    onChange={handleImageUrlChange}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AddToPhotosIcon />}
                    onClick={openUploader}
                    disabled={busy}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Set image
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="unit-localization-heading"
            sx={cardSx}
          >
            <CardHeading
              id="unit-localization-heading"
              title="Localized details"
              helper="Provide names and descriptions for each language."
            />
            <Grid container spacing={3}>
              {localized("unitName", "Name")}
              {localized("unitAbbreviation", "Abbreviation")}
              {localized("unitDisplayName", "Display name", true)}
              {localized("unitDescription", "Description", true)}
              {localized("unitToolTip", "Tooltip", true)}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      {onRefresh && (
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<RefreshIcon />}
            disabled={busy}
            onClick={handleRefresh}
          >
            Refresh unit
          </Button>
        </Box>
      )}
      <StickyFormActions
        hasChanges={hasChanges}
        saving={busy}
        saveText={props.saveText}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </Box>
  );
};
